import {useCallback, useMemo} from 'react';

import {useSelector} from '../../state/hooks';
import {usePanelBankSectionsPagingContext} from '../../state/panelbank/PanelBankSectionsPagingContext';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {getBoxDimensions, getPagingParams} from '../../util/panelbankFlow';
import {useFullFlowConfig} from '../../util/sectionFlowConfig';
import {usePanelBankWidthContext} from './PanelBankWidthContext';
import {useWorkspaceSectionContext} from './WorkspaceSectionContextProvider';

/**
 * This provides the metadata for the flow layout like how many
 * panels per page, how many panels per row, the max page, etc.
 */
export const useWorkspaceFlowLayout = ({
  numTotalPanelsInSection,
  panelBankSectionConfigRef,
}: {
  numTotalPanelsInSection: number;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
}) => {
  const {panelBankWidth} = usePanelBankWidthContext();

  const flowConfig = useWorkspaceFlowConfig(panelBankSectionConfigRef);

  // Since this is a flow config, we want to snap panels
  // based on panel bank width. Panels within a section
  // should fill the entire section.
  const {boxWidth: panelWidth, boxHeight: panelHeight} = useMemo(
    () => getBoxDimensions(panelBankWidth, flowConfig),
    [panelBankWidth, flowConfig]
  );

  const {panelsPerPage, panelsPerRow, maxPage} = useMemo(() => {
    return getPagingParams({
      panelBankWidth,
      panelCount: numTotalPanelsInSection,
      flowConfig,
    });
  }, [panelBankWidth, numTotalPanelsInSection, flowConfig]);

  return {
    columnsPerPage: flowConfig.columnsPerPage,
    gutterWidth: flowConfig.gutterWidth,
    maxPage,
    panelHeight,
    panelsPerPage,
    panelsPerRow,
    panelWidth,
    rowsPerPage: flowConfig.rowsPerPage,
  };
};

export const useWorkspaceFlowConfig = (
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) => {
  const {sectionName} = useWorkspaceSectionContext();

  // This is what's in the spec
  const sectionFlowConfig = useSelector(
    state =>
      state.views.parts[panelBankSectionConfigRef.type][
        panelBankSectionConfigRef.id
      ].flowConfig
  );

  // If spec doesn't have a specified flow config, we use the default flow config
  return useFullFlowConfig(sectionName, sectionFlowConfig);
};

/**
 * This determines which page the flow section is on. It will clamp the current page to the max page.
 */
export const useWorkspaceFlowCurrentPage = ({
  panelBankSectionConfigRef,
  maxPage,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  maxPage: number;
}) => {
  const {currentPageBySectionRefID, setCurrentPageForSectionRefID} =
    usePanelBankSectionsPagingContext();

  const setCurrentPage = useCallback(
    (newCurrentPage: number) => {
      // If you're on, say, page 3, and then you filter panels but there's only 1 page of results,
      // currentPage will be greater than maxPage, so we clamp it here
      setCurrentPageForSectionRefID(
        panelBankSectionConfigRef.id,
        newCurrentPage > maxPage ? maxPage : newCurrentPage
      );
    },
    [maxPage, panelBankSectionConfigRef.id, setCurrentPageForSectionRefID]
  );

  const currentPage = useMemo(
    () => currentPageBySectionRefID[panelBankSectionConfigRef.id] ?? 0,
    [panelBankSectionConfigRef.id, currentPageBySectionRefID]
  );

  return {
    currentPage,
    setCurrentPage,
  };
};

/**
 * This determines the height of the flow layout based on pagination and panel dimensions.
 */
export const useWorkspaceFlowLayoutHeight = ({
  gutterWidth,
  numTotalPanelsInSection,
  panelHeight,
  panelsPerRow,
  rowsPerPage,
}: {
  gutterWidth: number;
  numTotalPanelsInSection: number;
  panelHeight: number;
  panelsPerRow: number;
  rowsPerPage: number;
}) => {
  return useMemo(() => {
    const panelHeightWithOffset = gutterWidth + panelHeight;

    if (numTotalPanelsInSection <= panelsPerRow) {
      return panelHeightWithOffset;
    }

    return rowsPerPage * panelHeightWithOffset;
  }, [
    numTotalPanelsInSection,
    panelsPerRow,
    rowsPerPage,
    gutterWidth,
    panelHeight,
  ]);
};
